import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Space, Button, Table, Typography, Flex, Modal, Input, Tooltip } from "antd";
import { EditOutlined, DeleteOutlined, SearchOutlined, PlusOutlined, CloseOutlined, SwapOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { deleteUser, getGroupByTenant, getUsers, searchUsers } from "../../../../services/adminServices";
import { showLoader } from "../../../../utils/showloader";
import AddEditUser from "./AddEditUser";
import UserDetailScreen from "./UserDetailScreen";
import { setFormType } from "../../../../redux/slices/roleUserSlice";
import Paginator from "../../../space/components/table/Paginator";

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

const UserManagement = (props) => {
    const { submitUser, setSubmitUser } = props;

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [showForm, setShowForm] = useState(false)
    const [users, setUsers] = useState([])
    const [groups, setGroups] = useState([])
    const { formType } = useSelector(state => state.roleUser);
    const [selectedUser, setSelectedUser] = useState(null)
    const [showSearch, setShowSearch] = useState(false)
    const [searchText, setSearchText] = useState("")
    const [appliedSearch, setAppliedSearch] = useState("")
    const [showUserDetail, setShowUserDetail] = useState(false)
    const [userDetailRecord, setUserDetailRecord] = useState(null)
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [totalCount, setTotalCount] = useState(0)


    useEffect(() => {
        if (formType === "user") {
            setShowForm(true)
        } else {
            setShowForm(false)
        }
    }, [formType])

    useEffect(() => {
        const timer = setTimeout(() => {
            const next = searchText.trim()
            const nextApplied = next.length >= MIN_SEARCH_LENGTH ? next : ""
            setAppliedSearch((prev) => {
                if (prev !== nextApplied) {
                    setPage(1)
                }
                return nextApplied
            })
        }, SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [searchText])

    const fetchUsers = async (currentPage = page, size = pageSize, query = appliedSearch, signal) => {
        showLoader(true)
        try {
            if (query) {
                const results = await searchUsers(query, signal)
                if (signal?.aborted) return
                setUsers(results)
                setTotalCount(results.length)
            } else {
                const data = await getUsers(currentPage, size)
                if (signal?.aborted) return
                setUsers(data.users)
                setTotalCount(data.totalCount)
            }
        } finally {
            if (!signal?.aborted) {
                showLoader(false)
            }
        }
    }

    useEffect(() => {
        const controller = new AbortController()
        fetchUsers(page, pageSize, appliedSearch, controller.signal)
        return () => controller.abort()
    }, [page, pageSize, appliedSearch])

    useEffect(() => {
        if (!props.isEdit) {
            setSelectedUser(null)
        }
    }, [props.isEdit])

    const fetchGroups = async () => {
        showLoader(true)
        let data = await getGroupByTenant();
        const mappedGroups = data.groups.map((item) => {
            return { ...item, email: item.users.map(val => val.email).join(', ') }
        })
        showLoader(false)
        setGroups(mappedGroups);
    }

    useEffect(() => {
        fetchGroups();
    }, [])


    const handleEditUser = (record) => {
        dispatch(setFormType('user'))
        setSelectedUser({ ...record })
        props.setIsEdit(true)
    }

    const handleDeleteUser = async (record) => {
        showLoader(true)
        await deleteUser(record.userId)
        fetchUsers()
        showLoader(false)
    }

    const confirmUserDelete = (record) => {
        Modal.confirm({
            title: t("Confirm"),
            content: t("Deleting the user will affect the spaces and workflows using this user. Please check the 'User to spaces mapping' section before proceeding. Are you sure you want to delete this user?"),
            okText: t("Yes"),
            cancelText: t("No"),
            onOk: () => {
                handleDeleteUser(record)
                Modal.destroyAll()
            },
            footer: (_, { OkBtn, CancelBtn }) => (
                <>
                    <CancelBtn />
                    <OkBtn />
                </>
            ),
        });
    }

    const reloadTable = () => {
        fetchUsers();
    }

    const handleUserMap = (record) => {
        setUserDetailRecord(record)
        setShowUserDetail(true)
    }


    const columns = [
        {
            title: t("Name"),
            dataIndex: "name",
            key: "name",
            width: "20%"
        },
        {
            title: t("Email ID"),
            dataIndex: "email",
            key: "email",
            width: "20%",
        },
        {
            title: t("Description"),
            dataIndex: "description",
            key: "Description",
            width: "40%",
        },
        {
            title: t('Action'),
            dataIndex: "",
            width: "10%",
            render: (text, record, rowIndex) => {
                return (
                    <Space>
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEditUser(record, rowIndex)}
                        />
                        <Button
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => confirmUserDelete(record)}
                        />
                        <Tooltip title={t("User to spaces mapping")}>
                            <Button size="small" icon={<SwapOutlined />} onClick={() => handleUserMap(record, rowIndex)} />
                        </Tooltip>
                    </Space>
                )
            },
        }
    ]

    const locale = {
        emptyText: appliedSearch ? (
            <span>
                <p style={{ fontSize: "1rem", fontWeight: "600", color: "#000", marginBottom: "16px" }}>{t("No users found")}</p>
            </span>
        ) : (
            <span>
                {/* <img src="/assets/RoleTable.svg" /> */}
                <p style={{ fontSize: "1rem", fontWeight: "600", color: "#000", marginBottom :"16px" }}>{t("Add new user to manage the user data")}</p>
                <Button size="large" className="submit-button" icon={<PlusOutlined />} onClick={() => setShowForm(true)}>{t("New")}</Button>
            </span>
        ),
    };

    const handleAddUser = () => {
        setSelectedUser(null)
        dispatch(setFormType("user"))
        setShowForm(true)
    }

    const handleSearchToggle = () => {
        setShowSearch(!showSearch)
        if (showSearch) {
            setSearchText("")
            setAppliedSearch("")
        }
    }

    return (
        <div className="tab-content">
            {
                showUserDetail ?
                    <UserDetailScreen
                        selectedUser={userDetailRecord}
                        onBack={() => {
                            setShowUserDetail(false)
                            setUserDetailRecord(null)
                        }}
                    />
                : !showForm ?
                    <>
                        <Flex align="center" justify="space-between">
                            <Typography.Title level={4} style={{ margin: 0, padding: 0 }}>{t("User Management")}</Typography.Title>
                            <Flex gap={8} align="center" justify="flex-end">
                                {showSearch && (
                                    <Input
                                        placeholder={t("Search users by name or email")}
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        style={{ width: 250 }}
                                        allowClear
                                        size="large"
                                    />
                                )}
                                <Button
                                    size="large"
                                    icon={showSearch ? <CloseOutlined /> : <SearchOutlined />}
                                    onClick={handleSearchToggle}
                                    type={"default"}
                                />
                                <Button size="large" className="submit-button" icon={<PlusOutlined />} onClick={handleAddUser}>{t('Add User')}</Button>
                            </Flex>
                        </Flex>
                        <div className="table-content">
                            <Table
                                className="white-header-table"
                                locale={locale}
                                columns={columns}
                                dataSource={users}
                                pagination={false}
                                rowKey={(record) => record.userId || record.email}
                            />
                            {appliedSearch ? (
                                users.length > 0 && (
                                    <div className="flex justify-between items-center" style={{ marginTop: 16 }}>
                                        <div className="text-[#000000a9]">
                                            {t("Showing")} {users.length} {t("matching users")}
                                        </div>
                                    </div>
                                )
                            ) : totalCount > 0 && (
                                <div className="flex justify-between items-center" style={{ marginTop: 16 }}>
                                    <div className="text-[#000000a9]">
                                        {t("Showing")} {(page - 1) * pageSize + 1} {t("to")} {Math.min(page * pageSize, totalCount)} {t("of")} {totalCount} {t("users")}
                                    </div>
                                    <Paginator
                                        total={totalCount}
                                        currentPage={page}
                                        pageSize={pageSize}
                                        onChange={(newPage, newPageSize) => {
                                            setPage(newPage)
                                            setPageSize(newPageSize)
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                    </>
                    : <AddEditUser groups={groups} selectedUser={selectedUser} setShowForm={setShowForm} showForm={showForm} submitUser={submitUser} reloadTable={reloadTable} setSubmitUser={setSubmitUser} />

            }
        </div>
    )
}

export default UserManagement